// SPDX-FileCopyrightText: 2016-2026 Lari Natri <lari.natri@iki.fi>
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * Promise-based browser client for the app-scoped SongIndex worker.
 *
 * Construct once in main.ts and inject it into views/services that need song
 * data. The worker and JSON data are loaded lazily on init() or on the first
 * operation such as search() or getSongsBySongId().
 */
import type { InitialState } from "../../shared/src/types";
import { getBookViewId, getEnabledBooks, resolveBookJsonUrl } from "../../shared/src/songbook";
import type {
  BookInit,
  ExactSongIdResult,
  IndexedSong,
  SearchHit,
  SearchOpts,
  SongDetails,
  SongIndexEntry,
  SongIndexStats,
} from "../../shared/src/song-index";
import type {
  SongIndexWorkerRequest,
  SongIndexWorkerResponse,
} from "../../shared/src/song-index-messages";
import type { GroupedSongResult } from "../../shared/src/song-results";

type InitListener = (stats: SongIndexStats) => void;
type ProgressListener = (loaded: number, total: number) => void;

/** Browser facade that turns worker messages into typed Promise APIs. */
export class SongIndexClient {
  private worker: Worker | null = null;
  private readonly books: BookInit[];
  private readyValue = false;
  private initPromise: Promise<SongIndexStats> | null = null;
  private nextRequestId = 1;
  private pending = new Map<
    number,
    { resolve: (value: any) => void; reject: (reason: unknown) => void }
  >();
  private initListeners = new Set<InitListener>();
  private progressListeners = new Set<ProgressListener>();
  private latestStatsValue: SongIndexStats = {
    variants: [],
    sources: [],
    songCount: 0,
    bookCount: 0,
    errorCount: 0,
    songIdCount: 0,
  };

  /** Derives BookInit entries from InitialState; no worker is started yet. */
  constructor(state: InitialState) {
    this.books = this.buildInitBooks(state);
  }

  get ready(): boolean {
    return this.readyValue;
  }

  /** Latest known stats; empty until init completes. */
  get latestStats(): SongIndexStats {
    return {
      ...this.latestStatsValue,
      variants: [...this.latestStatsValue.variants],
      sources: [...this.latestStatsValue.sources],
    };
  }

  /** Subscribes to successful init/reinit; returns an unsubscribe function. */
  onInitDone(listener: InitListener): () => void {
    this.initListeners.add(listener);
    if (this.readyValue) listener(this.latestStats);
    return () => this.initListeners.delete(listener);
  }

  /** Subscribes to per-book loading progress during initialization. */
  onProgress(listener: ProgressListener): () => void {
    this.progressListeners.add(listener);
    return () => this.progressListeners.delete(listener);
  }

  /** Lazily starts the worker and builds indexes; force rebuilds existing data. */
  init(force = false): Promise<SongIndexStats> {
    if (this.initPromise) return this.initPromise;
    if (!force && this.readyValue) return Promise.resolve(this.latestStats);
    if (this.books.length === 0) {
      this.readyValue = true;
      this.latestStatsValue = {
        variants: [],
        sources: [],
        songCount: 0,
        bookCount: 0,
        errorCount: 0,
        songIdCount: 0,
      };
      this.notifyInitDone();
      return Promise.resolve(this.latestStats);
    }

    this.ensureWorker();
    const requestId = this.nextRequestId++;
    this.initPromise = new Promise<SongIndexStats>((resolve, reject) => {
      this.pending.set(requestId, { resolve, reject });
      this.worker?.postMessage({
        type: "init",
        requestId,
        books: this.books,
        force,
      } satisfies SongIndexWorkerRequest);
    }).finally(() => {
      this.initPromise = null;
    });
    return this.initPromise;
  }

  /** Searches the index, initializing it first if needed. */
  async search(
    query: string,
    opts: SearchOpts,
  ): Promise<{
    queryNorm: string;
    hits: SearchHit[];
    groups: GroupedSongResult[];
    truncated: boolean;
  }> {
    await this.init();
    return this.request({
      type: "search",
      requestId: this.nextRequestId++,
      query,
      opts,
      books: this.books,
    });
  }

  /** Fetches full song details for a search result ref. */
  async getSongDetailsByRef(ref: number): Promise<SongDetails | null> {
    await this.init();
    return this.request({
      type: "song-details",
      requestId: this.nextRequestId++,
      ref,
      books: this.books,
    });
  }

  /** Gets all variants/books for a song id, initializing the index if needed. */
  async getSongsBySongId(songId: string): Promise<readonly IndexedSong[]> {
    await this.init();
    return this.request({
      type: "songs-by-song-id",
      requestId: this.nextRequestId++,
      songId,
      books: this.books,
    });
  }

  /** Gets grouped appearances for an exact stable ID, independently of normalized PDF lookup. */
  async lookupExactSongId(songId: string): Promise<ExactSongIdResult> {
    const stats = await this.init(this.readyValue && this.latestStatsValue.errorCount > 0);
    if (stats.errorCount > 0) throw new Error("Song metadata is incomplete.");
    return this.request({
      type: "exact-song-id",
      requestId: this.nextRequestId++,
      songId,
      books: this.books,
    });
  }

  /** Gets one display row per song id for the Index view. */
  async getSongIndexEntries(): Promise<SongIndexEntry[]> {
    await this.init();
    return this.request({
      type: "song-index-entries",
      requestId: this.nextRequestId++,
      books: this.books,
    });
  }

  /** Gets current index stats, initializing the index if needed. */
  async getStats(): Promise<SongIndexStats> {
    await this.init();
    return this.request({ type: "stats", requestId: this.nextRequestId++, books: this.books });
  }

  /** Sends one request/response style message to the worker. */
  private request<T>(msg: SongIndexWorkerRequest & { requestId: number }): Promise<T> {
    this.ensureWorker();
    return new Promise<T>((resolve, reject) => {
      this.pending.set(msg.requestId, { resolve, reject });
      this.worker?.postMessage(msg);
    });
  }

  /** Creates the worker on first use. */
  private ensureWorker(): void {
    if (this.worker) return;
    const worker = new Worker(new URL("./song-index-worker.js", import.meta.url), {
      type: "module",
    });
    worker.addEventListener("message", (ev: MessageEvent<SongIndexWorkerResponse>) =>
      this.handleMessage(ev.data),
    );
    worker.addEventListener("error", (ev: ErrorEvent) =>
      this.rejectAll(new Error(String(ev.message || "Song index worker error"))),
    );
    this.worker = worker;
  }

  /** Resolves/rejects pending promises and forwards progress events. */
  private handleMessage(msg: SongIndexWorkerResponse): void {
    if (msg.type === "init-progress") {
      this.progressListeners.forEach(fn => fn(msg.loaded, msg.total));
      return;
    }

    if (msg.type === "error") {
      if (msg.requestId != null) {
        const p = this.pending.get(msg.requestId);
        this.pending.delete(msg.requestId);
        p?.reject(new Error(msg.message));
      }
      return;
    }

    const requestId = (msg as any).requestId;
    if (requestId == null) return;
    const pending = this.pending.get(requestId);
    if (!pending) return;
    this.pending.delete(requestId);

    if (msg.type === "init-done") {
      this.readyValue = true;
      this.latestStatsValue = {
        variants: msg.variants,
        sources: msg.sources,
        songCount: msg.songCount,
        bookCount: msg.bookCount,
        errorCount: msg.errorCount,
        songIdCount: msg.songIdCount,
      };
      this.notifyInitDone();
      pending.resolve(this.latestStats);
    } else if (msg.type === "search-results") {
      pending.resolve({
        queryNorm: msg.queryNorm,
        hits: msg.hits,
        groups: msg.groups,
        truncated: msg.truncated,
      });
    } else if (msg.type === "song-details") {
      pending.resolve(msg.details);
    } else if (msg.type === "songs-by-song-id") {
      pending.resolve(msg.songs);
    } else if (msg.type === "exact-song-id") {
      pending.resolve(msg.result);
    } else if (msg.type === "song-index-entries") {
      pending.resolve(msg.entries);
    } else if (msg.type === "stats") {
      pending.resolve(msg.stats);
    }
  }

  private notifyInitDone(): void {
    const stats = this.latestStats;
    this.initListeners.forEach(fn => fn(stats));
  }

  private rejectAll(reason: unknown): void {
    for (const [id, p] of this.pending) {
      this.pending.delete(id);
      p.reject(reason);
    }
    this.initPromise = null;
  }

  /** Builds worker init input from enabled songbook definitions. */
  private buildInitBooks(state: InitialState): BookInit[] {
    const books = getEnabledBooks(state.books);
    return books.flatMap((book, idx0) => {
      const bookId = idx0 + 1;
      const jsonUrl = resolveBookJsonUrl(state.books, book);
      if (!jsonUrl) return [];
      return [
        {
          bookId,
          viewId: getBookViewId(book, bookId),
          jsonUrl,
          sourceId: (book as any).source_book_id || `book-${bookId}`,
          variant: (book as any).variant || "default",
        },
      ];
    });
  }
}
